import type {MaskOptsInterface} from '@Libraries/mask';
import {Mask} from '@Libraries/mask';
import {loadFabricObjectForShape} from '@PosterWhiteboard/items/vector-item/vector-item.library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {applyMaskForFabricObject, MaskingEffect} from '@PosterWhiteboard/classes/masking/masking.class';
import {applyFillToFabricObject} from '@Utils/fabric.util';
import type {MaskingShapeObject} from '@PosterWhiteboard/classes/masking/masking-shape.class';
import {setHasColorPop, setHasInsideMasking, setShapeId} from '@Panels/masking-panel/components/shape-masking-panel/shape-masking-panel-reducer';
import {setShowRemoveMask} from '@Panels/masking-panel/masking-panel-reducer';
import type {FabricObject, Group} from '@postermywall/fabricjs-2';
import {FabricImage, filters} from '@postermywall/fabricjs-2';
import {KeyboardShortcutScope} from './keyboard-shortcuts';

const DEFAULT_MASK_SHAPE_ID = '136';

export class ShapeMask extends Mask {
  public maskingShape?: FabricObject | Group;
  public insideMasking = false;
  public maskEffect: MaskingEffect = MaskingEffect.NONE;
  public ignoreDefaultMask = false;
  protected keyboardShortcutScope = KeyboardShortcutScope.ITEM_SHAPE_MASKING;
  private imageToMask!: FabricImage;
  private shapeID: string = DEFAULT_MASK_SHAPE_ID;

  declare initialMaskingData?: MaskingShapeObject;

  public async init(): Promise<void> {
    await super.init();
    await this.onInit();
  }

  private async onInit(): Promise<void> {
    if (this.canvas.backgroundImage) {
      this.canvas.backgroundImage.opacity = 0;
    }

    this.canvas.on({
      'object:moving': this.ghostShapeFill.bind(this),
      'object:scaling': this.ghostShapeFill.bind(this),
      'object:resizing': this.ghostShapeFill.bind(this),
      'object:rotating': this.ghostShapeFill.bind(this),
      'object:skewing': this.ghostShapeFill.bind(this),
      'object:modified': this.maskImage.bind(this),
    });
    this.ignoreDefaultMask = false;

    this.imageToMask = new FabricImage(this.loader, {
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      lockMovementX: true,
      lockMovementY: true,
      hasControls: false,
      hasBorders: false,
      cropX: this.cropData?.x || 0,
      cropY: this.cropData?.y || 0,
      padding: 0,
      perPixelTargetFind: false,
      selectable: false,
    });
    this.canvas.once('object:added', () => {
      void this.applyDefaultMask();
    });
    this.canvas.add(this.imageToMask);
  }

  public isMaskApplied(): boolean {
    return !!this.maskingShape;
  }

  public async onSelectionNudged(): Promise<void> {
    this.ghostShapeFill();
  }

  public async onNudgeItemEnd(): Promise<void> {
    await this.maskImage();
  }

  public async onUpdateKeyValue(): Promise<void> {
    await this.maskImage();
  }

  public removeMasking(): void {
    if (this.maskingShape) {
      this.canvas.remove(this.maskingShape);
      this.maskingShape = undefined;
      this.refreshBackgroundImage();
      this.hideMaskedImage();
      this.invalidate();
    }
  }

  public invalidate(): void {
    super.invalidate();
    const currentRemoveMaskState = window.PMW.redux.store.getState().masking.showRemoveMask;
    if (currentRemoveMaskState !== this.isMaskApplied()) {
      window.PMW.redux.store.dispatch(setShowRemoveMask(this.isMaskApplied()));
    }
    const currentShapeIDState = window.PMW.redux.store.getState().shapeMasking.shapeId;
    if (this.maskingShape && currentShapeIDState !== this.shapeID) {
      window.PMW.redux.store.dispatch(setShapeId(this.shapeID));
    }
    if (!this.maskingShape) {
      window.PMW.redux.store.dispatch(setShapeId(undefined));
    }
  }

  public async addNewShape(shapeId: string): Promise<void> {
    showLoading('addMaskingShape');
    this.shapeID = shapeId;
    try {
      const fabricObject = await loadFabricObjectForShape(this.shapeID);
      const previousShape = this.maskingShape;

      await this.addShapeToCanvas(fabricObject);
      if (previousShape) {
        const width = previousShape.getScaledWidth();
        fabricObject.scaleToWidth(width);
        fabricObject.set({
          left: previousShape.left,
          top: previousShape.top,
          angle: previousShape.angle,
        });
        this.canvas.remove(previousShape);
      } else if (this.maskingShape) {
        const scale = this.getDefaultItemScale();
        const canvasWidth = this.canvas.getWidth() / this.scale;
        const canvasHeight = this.canvas.getHeight() / this.scale;
        const width = this.maskingShape.width * scale;

        fabricObject.set({
          left: (canvasWidth - this.maskingShape.width * scale) / 2,
          top: (canvasHeight - this.maskingShape.height * scale) / 2,
          scaleX: 1,
          scaleY: 1,
        });
        fabricObject.scaleToWidth(width);
      }
      await this.maskImage();
      this.refreshBackgroundImage();
      this.invalidate();
    } finally {
      hideLoading('addMaskingShape');
    }
  }

  private addShapeToCanvas(fabricObject: FabricObject | Group): Promise<void> {
    return new Promise((resolve) => {
      this.setStroke(fabricObject, 0);
      fabricObject.setControlsVisibility({
        ml: true,
        mt: true,
        mr: true,
        mb: true,
      });
      fabricObject.set({
        selectable: true,
        lockSkewingX: true,
        lockSkewingY: true,
        lockUniScaling: false,
        perPixelTargetFind: false,
      });

      this.canvas.once('object:added', () => {
        this.maskingShape = fabricObject;
        this.canvas.discardActiveObject();
        this.canvas.setActiveObject(this.maskingShape);
        resolve();
      });
      this.canvas.add(fabricObject);
    });
  }

  public setStroke(fabricObject: FabricObject | Group, strokeWidth: number): void {
    let paths = [];
    if ('getObjects' in fabricObject) {
      paths = fabricObject.getObjects();
    } else {
      paths = [fabricObject];
    }

    for (let i = 0; i < paths.length; i++) {
      paths[i].set({
        strokeWidth,
      });
    }
  }

  public getDefaultItemScale(): number {
    if (!this.maskingShape) {
      throw new Error('No masking shape added');
    }

    let scale;
    const canvasWidth = this.canvas.getWidth() / this.scale;
    const canvasHeight = this.canvas.getHeight() / this.scale;
    const margin = 100 / this.scale;

    if ((canvasWidth - margin) / this.maskingShape.width < (canvasHeight - margin) / this.maskingShape.height) {
      scale = (canvasWidth - margin) / this.maskingShape.width;
    } else {
      scale = (canvasHeight - margin) / this.maskingShape.height;
    }

    return scale;
  }

  public async maskImage(): Promise<void> {
    if (this.maskingShape) {
      this.invalidate();
      this.maskingShape.setCoords();

      this.maskingShape.set({
        left: this.maskingShape.left + (this.cropData?.x || 0),
        top: this.maskingShape.top + (this.cropData?.y || 0),
      });
      this.canvas.renderAll();
      const maskedImage = await applyMaskForFabricObject(this.loader, this.maskingShape, {
        insideMasking: this.insideMasking,
        maskEffect: this.maskEffect,
      });
      this.maskingShape.set({
        left: this.maskingShape.left - (this.cropData?.x || 0),
        top: this.maskingShape.top - (this.cropData?.y || 0),
      });
      this.canvas.renderAll();

      this.imageToMask.setElement(maskedImage);
      this.showMaskedImage();
      applyFillToFabricObject(this.maskingShape, 'rgba(0,0,0,0)');
      this.invalidate();
    }
  }

  private showMaskedImage(): void {
    if (this.imageToMask) {
      this.imageToMask.set({
        opacity: 1,
      });
      if (this.canvas.backgroundImage) {
        this.canvas.backgroundImage.set({
          opacity: 0,
        });
      }
    }
  }

  private hideMaskedImage(): void {
    if (this.imageToMask) {
      this.imageToMask.set({
        opacity: 0,
      });
      if (this.canvas.backgroundImage) {
        this.canvas.backgroundImage.set({
          opacity: 1,
        });
      }
    }
  }

  public ghostShapeFill(): void {
    if (!this.maskingShape) {
      return;
    }

    this.hideMaskedImage();
    if (!this.insideMasking && this.maskEffect === MaskingEffect.COLOR_POP) {
      applyFillToFabricObject(this.maskingShape, 'rgba(255,255,255,0.3)');
    } else {
      applyFillToFabricObject(this.maskingShape, 'rgba(0,0,0,0.3)');
    }

    this.invalidate();
  }

  public applyDefaultMask(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        if (this.initialMaskingData) {
          this.restoreMask().then(resolve).catch(reject);
        } else {
          this.addNewShape(this.shapeID).then(resolve).catch(reject);
        }
      }, 250);
    });
  }

  private async restoreMask(): Promise<void> {
    if (this.initialMaskingData) {
      this.shapeID = this.initialMaskingData.shapeName;
      this.setInsideMasking(this.initialMaskingData.insideMasking);
      this.setMaskingEffect(this.initialMaskingData.maskEffect);
      const shape = await loadFabricObjectForShape(this.shapeID);
      shape.set({
        left: this.initialMaskingData.left * this.getScaleX(),
        top: this.initialMaskingData.top * this.getScaleY(),
        width: this.initialMaskingData.width,
        height: this.initialMaskingData.height,
        scaleX: this.initialMaskingData.scaleX * this.getScaleX(),
        scaleY: this.initialMaskingData.scaleY * this.getScaleY(),
        angle: this.initialMaskingData.angle,
      });
      await this.addShapeToCanvas(shape);
      await this.maskImage();
      this.refreshBackgroundImage();
      this.invalidate();
      hideLoading('addMaskingShape');
    }
  }

  public async toggleInsideMasking(): Promise<void> {
    this.setInsideMasking(!this.insideMasking);
    await this.maskImage();
  }

  public async toggleMaskingEffect(): Promise<void> {
    this.setMaskingEffect(this.maskEffect === MaskingEffect.NONE ? MaskingEffect.COLOR_POP : MaskingEffect.NONE);
    await this.maskImage();
  }

  private setInsideMasking(value: boolean): void {
    this.insideMasking = value;
    window.PMW.redux.store.dispatch(setHasInsideMasking(value));
    this.refreshBackgroundImage();
    this.invalidate();
  }

  private setMaskingEffect(value: MaskingEffect): void {
    this.maskEffect = value;
    window.PMW.redux.store.dispatch(setHasColorPop(value === MaskingEffect.COLOR_POP));
    this.refreshBackgroundImage();
    this.invalidate();
  }

  private refreshBackgroundImage(): void {
    if (!this.canvas.backgroundImage || !(this.canvas.backgroundImage instanceof FabricImage)) {
      return;
    }

    while (this.canvas.backgroundImage.filters.length) {
      this.canvas.backgroundImage.filters.pop();
    }

    if (this.maskingShape) {
      if (!this.insideMasking && this.maskEffect === MaskingEffect.COLOR_POP) {
        this.canvas.backgroundImage.filters.push(new filters.Grayscale());
      }
    }
    this.canvas.backgroundImage.applyFilters();
    this.invalidate();
  }
}

export const createShapeMask = async (id: string, url: string, parentSelector: string, opts: MaskOptsInterface = {}): Promise<ShapeMask> => {
  const shapeMask = new ShapeMask(id, url, parentSelector, opts);
  await shapeMask.init();
  return shapeMask;
};
